import type { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

/**
 * User Profile P1 — the one section card + info row the profile sections share
 * (replaces the private copies in the old ProfilePage, CompanyProfilePage and
 * PlatformProfilePage).
 */
export function ProfileSectionCard({
  title,
  description,
  icon,
  action,
  children,
  testId,
}: {
  title: string;
  description?: string;
  icon: IconDefinition;
  action?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section
      aria-label={title}
      data-testid={testId}
      className="rounded-xl border border-border bg-card"
    >
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FontAwesomeIcon icon={icon} className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground">{title}</h2>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
          </div>
        </div>
        {action}
      </div>
      <div className="p-4 sm:p-6">{children}</div>
    </section>
  );
}

export function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-4">
      <span className="w-40 shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
      <span className={`text-sm ${value ? 'text-foreground' : 'text-muted-foreground'}`}>
        {value || 'Not set'}
      </span>
    </div>
  );
}

export function ManagedByAdminNote({ children }: { children?: ReactNode }) {
  return (
    <p className="mt-3 text-xs text-muted-foreground">
      {children ?? 'Managed by your administrator. Contact them to request a change.'}
    </p>
  );
}
